// manages tutorial state and business logic 
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { TutorialContext } from './TutorialContext';
import tutorialConfig from './utils/tutorialConfig.json';
import sendServerLogging from '../../utils/sendServerLogging';
import userJSON from '../../utils/userJSON';
import { isStandaloneUser as getIsStandaloneUser } from "../../utils/standaloneUser";
import { getSessionToken } from "../../lib/sessionToken";
import { useFeatureGate } from '../../hooks/useFeatureGate';
import {
  POINT_OF_CARE_AVAILABILITY_EVENT,
  countVisibleSteps,
  isStepAvailable,
} from './utils/gatedSlides';

// Mock data for local development
const MOCK_TUTORIAL_DATA = {
  showOnLogin: true,
  platform: "web_app_gov",
  onboarding: {
    startedAt: null,
    completedAt: null,
    skippedAt: null,
    lastSectionId: null,
    lastStepId: null,
    sections: {}
  },
  pageTutorials: {}
};

const SLIDE_ACCESS_WAIT_TIMEOUT_MS = 3000;

// The server reads and rewrites the whole progress record on every PATCH, so
// overlapping writes can land out of order and undo each other (a late
// "start" restoring step 1 over a "skip"). Send them one at a time, in the
// order they were made. Module-level because every provider writes the same
// record. Each write is aborted after a timeout so one hung request can't
// hold every later write (a "skip" or "complete") behind it.
const PROGRESS_WRITE_TIMEOUT_MS = 10000;
let progressWriteQueue = Promise.resolve();
export const patchTutorialProgress = (body) => {
  const write = () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PROGRESS_WRITE_TIMEOUT_MS);
    return fetch(`${process.env.REACT_APP_API_URL}/tutorial/progress`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        token: getSessionToken(),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    }).finally(() => clearTimeout(timeoutId));
  };
  const result = progressWriteQueue.then(write, write);
  progressWriteQueue = result.catch(() => {});
  return result;
};

export const TutorialProvider = ({ 
  children, 
  mode = 'onboarding', // 'onboarding' or 'page'
  pageId = null,
  isSubscribed
}) => {
  const location = useLocation();
  const isStandaloneUser = getIsStandaloneUser();
  const vaSiteSwapGate = useFeatureGate('va-site-swap');
  const modelTypeGate = useFeatureGate('model-type-selector');
  // null until the Point of Care card has loaded its launch sites at least once.
  const [hasPointOfCareSites, setHasPointOfCareSites] = useState(null);
  const hasVaSiteSwapAccess = !vaSiteSwapGate.isLoading && vaSiteSwapGate.hasFeature('on');
  const hasPointOfCareAccess = hasVaSiteSwapAccess && hasPointOfCareSites === true;
  const hasLanguageModelAccess = !modelTypeGate.isLoading && modelTypeGate.hasFeature('on');
  const isSlideAccessLoading =
    vaSiteSwapGate.isLoading ||
    modelTypeGate.isLoading ||
    (hasVaSiteSwapAccess && hasPointOfCareSites === null);
  const slideAccess = useMemo(
    () => ({
      smartAssist: true,
      pointOfCare: hasPointOfCareAccess,
      languageModel: hasLanguageModelAccess,
    }),
    [hasPointOfCareAccess, hasLanguageModelAccess]
  );
  const slideAccessRef = useRef({ access: slideAccess, isLoading: isSlideAccessLoading });
  slideAccessRef.current = { access: slideAccess, isLoading: isSlideAccessLoading };

  useEffect(() => {
    const onAvailability = (event) => setHasPointOfCareSites(Boolean(event.detail?.available));
    window.addEventListener(POINT_OF_CARE_AVAILABILITY_EVENT, onAvailability);
    return () => window.removeEventListener(POINT_OF_CARE_AVAILABILITY_EVENT, onAvailability);
  }, []);
  const isAuthCallbackPath =
    location.pathname.startsWith("/auth/fhir/callback") ||
    location.pathname.startsWith("/auth/standalone");
  const tutorialEnabled = Boolean(isSubscribed) && !isStandaloneUser;
  const [isActive, setIsActive] = useState(false);
  // The slide list is decided when the tour starts and held for its run. A
  // flag that resolves later must not add slides mid-tour: a user already past
  // that point would never see them, and the counter would shift under them.
  // Declared before the flatten effect so it is set in the same commit.
  const activeSlideAccessRef = useRef(null);
  useEffect(() => {
    activeSlideAccessRef.current = isActive ? slideAccessRef.current.access : null;
  }, [isActive]);
  // Auto-start holds for the same bounded wait startTutorial uses, so a
  // first-time tour isn't frozen before its flags have resolved.
  const [hasAutoStartWaitElapsed, setHasAutoStartWaitElapsed] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [currentStepId, setCurrentStepId] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [tutorialSteps, setTutorialSteps] = useState([]);
  const [tutorialData, setTutorialData] = useState(MOCK_TUTORIAL_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [flattenedSteps, setFlattenedSteps] = useState([]);
  const [patientDescription, setPatientDescription] = useState(null);
  const [shouldAutoStart, setShouldAutoStart] = useState(false);
  useEffect(() => {
    if (!shouldAutoStart) {
      setHasAutoStartWaitElapsed(false);
      return undefined;
    }
    const timeoutId = setTimeout(
      () => setHasAutoStartWaitElapsed(true),
      SLIDE_ACCESS_WAIT_TIMEOUT_MS
    );
    return () => clearTimeout(timeoutId);
  }, [shouldAutoStart]);
  const [isManualStart, setIsManualStart] = useState(false);
  const [startedFromDashboard, setStartedFromDashboard] = useState(false);
  // True only when the complete onboarding tour is running (all sections),
  // false for scoped subsets (Smart Chart / Review / Settings). Set explicitly
  // at each start path instead of inferring from the steps that are loaded.
  const [isFullTutorial, setIsFullTutorial] = useState(false);
  const previousPathnameRef = useRef(location.pathname);
  const forcedStepRef = useRef(null);
  const forcedStepTimestampRef = useRef(0);
  const hasFetchedRef = useRef(false);
  const fetchTutorialProgressRef = useRef(null);
  const flattenedStepsRef = useRef([]);

  // Define fetchTutorialProgress first so it's available for effects
  const fetchTutorialProgress = async () => {
    if (isAuthCallbackPath) {
      setIsActive(false);
      setShouldAutoStart(false);
      setIsManualStart(false);
      setTutorialSteps([]);
      setFlattenedSteps([]);
      setIsLoading(false);
      return;
    }
    if (!tutorialEnabled) {
      setIsLoading(false);
      return;
    }
    // Don't fetch if user is not authenticated
    if (!getSessionToken()) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('type', mode);
      if (mode === 'page' && pageId) {
        params.append('pageId', pageId);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/tutorial/progress?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            token: getSessionToken(),
          },
          cache: 'no-store', // Fetch API cache option
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tutorial progress: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch tutorial progress');
      }

      const data = result.data;

      // Merge with default structure if data is incomplete
      // Preserve showOnLogin from data if it exists, otherwise use default
      const mergedData = {
        ...MOCK_TUTORIAL_DATA,
        ...data,
        showOnLogin: data.showOnLogin !== undefined ? data.showOnLogin : MOCK_TUTORIAL_DATA.showOnLogin,
        onboarding: {
          ...MOCK_TUTORIAL_DATA.onboarding,
          ...(data.onboarding || {}),
        },
        pageTutorials: data.pageTutorials || {},
      };
      
      // For new users (no onboarding data), ensure showOnLogin is true
      // This ensures the full tutorial auto-starts for first-time users
      // Explicitly check for null/undefined values
      const hasOnboardingProgress = data.onboarding && (
        (data.onboarding.completedAt !== null && data.onboarding.completedAt !== undefined) ||
        (data.onboarding.lastStepId !== null && data.onboarding.lastStepId !== undefined) ||
        (data.onboarding.startedAt !== null && data.onboarding.startedAt !== undefined) ||
        // A skipped/closed tour counts as engaged — do not force showOnLogin
        // back on, otherwise the guide re-pops on every reload after the user
        // dismisses the first-time auto-launch.
        (data.onboarding.skippedAt !== null && data.onboarding.skippedAt !== undefined)
      );
      
      if (!hasOnboardingProgress) {
        mergedData.showOnLogin = true;
      }
      
      setTutorialData(mergedData);

      // Auto-start the onboarding tour ONLY for first-time users: no
      // completion and no prior progress of any kind. Once a user has seen
      // the tour (even if they closed it on the first slide), it never
      // auto-opens again — they can relaunch it from the "i" button.
      const shouldAutoStartTutorial =
        mode === 'onboarding' &&
        mergedData.showOnLogin &&
        (mergedData.onboarding.completedAt === null || mergedData.onboarding.completedAt === undefined) &&
        (mergedData.onboarding.skippedAt === null || mergedData.onboarding.skippedAt === undefined) &&
        (mergedData.onboarding.lastStepId === null || mergedData.onboarding.lastStepId === undefined);
      
      if (shouldAutoStartTutorial) {
        setShouldAutoStart(true);
        setIsFullTutorial(true);
        setTutorialSteps(tutorialConfig.sections);
      } else {
        // If tutorial was in progress, restore the saved position
        if (mergedData.onboarding.lastSectionId && mergedData.onboarding.lastStepId) {
          const section = tutorialConfig.sections.find(s => s.id === mergedData.onboarding.lastSectionId);
          if (section) {
            const stepIndex = section.steps.findIndex(s => s.id === mergedData.onboarding.lastStepId);
            if (stepIndex !== -1) {
              // User was in the middle of a tutorial, restore their position
              setTutorialSteps([section]);
              setCurrentSectionId(section.id);
              setCurrentStepId(mergedData.onboarding.lastStepId);
              setCurrentStepIndex(stepIndex);
            }
          }
        } else if (mergedData.onboarding.completedAt) {
          // Tutorial was completed, don't auto-start
          setShouldAutoStart(false);
        }
      }
      
      // For page-specific tutorials, load the relevant section
      if (mode === 'page' && pageId) {
        const pageSection = tutorialConfig.sections.find(s => s.id === pageId);
        if (pageSection) {
          setTutorialSteps([pageSection]);
        }
      }
      
    } catch (error) {
      console.error('[TutorialProvider] Error fetching tutorial progress:', error);
      // Fallback to mock data on error
      setTutorialData(MOCK_TUTORIAL_DATA);
    } finally {
      setIsLoading(false);
    }
  };

  // Store in ref so it's available in effects
  fetchTutorialProgressRef.current = fetchTutorialProgress;

  // Watch for navigation changes (which happen after login) and fetch when token is available
  useEffect(() => {
    if (isAuthCallbackPath) {
      setIsActive(false);
      setShouldAutoStart(false);
      setIsManualStart(false);
      setTutorialSteps([]);
      setFlattenedSteps([]);
      setIsLoading(false);
      return;
    }
    if (!tutorialEnabled) {
      setIsActive(false);
      setShouldAutoStart(false);
      setIsManualStart(false);
      setTutorialSteps([]);
      setFlattenedSteps([]);
      setIsLoading(false);
      return;
    }
    // Only fetch if we have a token and haven't fetched yet
    // This will trigger after login when user navigates to dashboard
    if (getSessionToken() && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchTutorialProgress().catch((err) => {
        console.error('[TutorialProvider] fetchTutorialProgress failed:', err);
        hasFetchedRef.current = false; // Reset on error so we can retry
      });
    } else if (!getSessionToken()) {
      hasFetchedRef.current = false; // Reset if token is removed
    }
  }, [isAuthCallbackPath, location.pathname, tutorialEnabled]); // Watch location changes (which happen after login)

  // Also run when mode or pageId changes (but only if we've already fetched)
  useEffect(() => {
    if (tutorialEnabled && hasFetchedRef.current && getSessionToken()) {
      fetchTutorialProgress();
    }
  }, [mode, pageId, tutorialEnabled]);

  // Check if there's a patient with "Ready to Record" status
  const hasReadyToRecordPatient = () => {
    // Check if there's an element with id="ready_to_record" in the DOM
    const readyToRecordElement = document.querySelector('#ready_to_record');
    return !!readyToRecordElement;
  };

  // Flatten the tutorial structure for easier navigation
  const flattenTutorialSteps = (sections) => {
    const flattened = [];
    const isFullTutorial = sections && sections.length === tutorialConfig.sections.length;
    // The guided dashboard walkthrough creates its own "Ready to Record" test
    // patient (the "Add a Veteran" step), so it must keep the steps that need
    // one — precharting_intro (which opens the Smart Chart), ready_to_record,
    // and veteran_consent — even before that patient exists. Detect that by the
    // presence of the patient-creation step so the slide count is stable (17,
    // not 14) from the very first slide.
    const willCreateTestPatient =
      Array.isArray(sections) &&
      sections.some(section =>
        (section.steps || []).some(
          step =>
            step.id === 'add_veteran' || step.id === 'add_veteran_modal_instructions'
        )
      );
    sections.forEach(section => {
      section.steps.forEach(step => {
        // Skip ready_to_record, veteran_consent, and precharting_intro only for page/section tutorials when no "Ready to Record" patient exists AND the tour won't create one itself.
        // For the full tutorial (or any tour that creates the test patient), always include these steps so we don't jump from na_declined straight to telehealth.
        if (
          (step.id === 'ready_to_record' ||
            step.id === 'veteran_consent' ||
            step.id === 'precharting_intro') &&
          !isFullTutorial &&
          !hasReadyToRecordPatient() &&
          !willCreateTestPatient
        ) {
          return;
        }
        if (!isStepAvailable(step.id, activeSlideAccessRef.current || slideAccess)) {
          return;
        }
        flattened.push({
          sectionId: section.id,
          stepId: step.id,
          ...step
        });
      });
    });
    return flattened;
  };

  const updateProgress = async (sectionId, stepId, action = 'view_step') => {
    if (!tutorialEnabled) {
      return;
    }
    // Seeing the first Smart Chart slide through ANY path (onboarding tour,
    // "i" launcher, or first-open auto-launch) counts as having seen the
    // Smart Chart training, so it never auto-opens again.
    if (
      stepId === 'precharting_refetch' &&
      !tutorialData?.pageTutorials?.smart_chart?.viewed
    ) {
      await markPageTutorialViewed('smart_chart');
    }
    const body = {
      action: action,
      stepId: stepId
    };
    
    if (mode === 'onboarding') {
      body.sectionId = sectionId;
    } else {
      body.pageId = pageId;
    }
    
    try {
      const response = await patchTutorialProgress(body);

      if (!response.ok) {
        throw new Error(`Failed to update tutorial progress: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update tutorial progress');
      }

      // Update local state to reflect server changes
      if (mode === 'onboarding') {
        setTutorialData(prev => ({
          ...prev,
          onboarding: {
            ...prev.onboarding,
            lastSectionId: sectionId,
            lastStepId: stepId,
            startedAt: prev.onboarding.startedAt || new Date().toISOString(),
            sections: {
              ...prev.onboarding.sections,
              [sectionId]: {
                viewed: true,
              },
            },
          },
        }));
      } else if (mode === 'page' && pageId) {
        setTutorialData(prev => ({
          ...prev,
          pageTutorials: {
            ...prev.pageTutorials,
            [pageId]: {
              viewed: true,
              count: (prev.pageTutorials[pageId]?.count || 0) + 1,
            },
          },
        }));
      }
    } catch (error) {
      // Still update local state even if API call fails
      if (mode === 'onboarding') {
        setTutorialData(prev => ({
          ...prev,
          onboarding: {
            ...prev.onboarding,
            lastSectionId: sectionId,
            lastStepId: stepId,
            startedAt: prev.onboarding.startedAt || new Date().toISOString(),
          },
        }));
      }
    }
  };

  // Record that a page-scoped tutorial (e.g. the Smart Chart slides) has been
  // shown, so it only ever auto-opens once per user.
  const markPageTutorialViewed = async (viewedPageId) => {
    if (!tutorialEnabled || !viewedPageId) {
      return;
    }
    setTutorialData(prev => ({
      ...prev,
      pageTutorials: {
        ...(prev.pageTutorials || {}),
        [viewedPageId]: {
          viewed: true,
          count: ((prev.pageTutorials || {})[viewedPageId]?.count || 0) + 1,
        },
      },
    }));
    try {
      await patchTutorialProgress({
        action: 'view_step',
        stepId: '',
        pageId: viewedPageId,
      });
    } catch (error) {
      // Local state is already updated; the flag will persist on a later visit.
    }
  };

  const startTutorial = async (options = {}) => {
    const shouldResume = Boolean(options?.resume);
    const requestedStartSectionId = options?.startSectionId || null;
    const requestedStartStepId = options?.startStepId || null;
    // Callers pass the raw steps they just loaded as expectedSteps when some of
    // them are gated, so the count is taken against the same slide access
    // flattenTutorialSteps is filtering with right now, not the access the
    // caller saw when it was clicked.
    const expectedSteps = options?.expectedSteps ?? null;
    const fixedExpectedStepCount = options?.expectedStepCount ?? null;
    const getExpectedStepCount = () =>
      expectedSteps
        ? countVisibleSteps(expectedSteps, slideAccessRef.current.access)
        : fixedExpectedStepCount;
    const hasRequestedStart =
      Boolean(requestedStartSectionId) && Boolean(requestedStartStepId);
    // Full-flow only when the caller opts in; every scoped subset omits it.
    setIsFullTutorial(Boolean(options?.isFullTutorial));
    // Mark as manual start to prevent auto-initialization
    setIsManualStart(true);

    // Let the feature flags behind the gated slides settle first. Bounded: a
    // flag still unresolved after this is treated as off for this run, the
    // same way the filter treats it, and the list is then held (see
    // activeSlideAccessRef) rather than growing later.
    for (
      let waited = 0;
      slideAccessRef.current.isLoading && waited < SLIDE_ACCESS_WAIT_TIMEOUT_MS;
      waited += 50
    ) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Wait for flattenedSteps to be populated (and requested step to exist when provided).
    let attempts = 0;
    const maxAttempts = 40; // 40 * 50ms = 2 seconds max wait
    const hasRequiredStep = (steps) => {
      // When the caller says how many steps it just loaded, wait until the
      // flattened list matches — a stale list from a previous run can contain
      // the requested step too, so existence alone is not enough.
      const expectedStepCount = getExpectedStepCount();
      if (expectedStepCount !== null && steps.length !== expectedStepCount) {
        return false;
      }
      if (!hasRequestedStart) {
        return true;
      }
      return steps.some(
        (step) =>
          step.sectionId === requestedStartSectionId &&
          step.stepId === requestedStartStepId
      );
    };
    while (
      (flattenedStepsRef.current.length === 0 ||
        !hasRequiredStep(flattenedStepsRef.current)) &&
      attempts < maxAttempts
    ) {
      await new Promise(resolve => setTimeout(resolve, 50));
      attempts++;
    }
    const currentFlattened = flattenedStepsRef.current;
    
    // If the flattened steps still don't satisfy the required-step guard after
    // waiting (empty list, wrong step count, or missing the requested step),
    // don't start — proceeding would launch a stale/mismatched tutorial.
    if (currentFlattened.length === 0 || !hasRequiredStep(currentFlattened)) {
      setIsActive(false);
      setIsManualStart(false);
      return;
    }
    
    // Get the section ID of the first step in the current tutorial
    const newSectionId = currentFlattened[0]?.sectionId;
    
    // Reset state if starting a different section
    const isDifferentSection = currentSectionId && currentSectionId !== newSectionId;
    if (isDifferentSection) {
      setCurrentStepIndex(0);
      setCurrentSectionId(null);
      setCurrentStepId(null);
    }
    
    setIsActive(true);
    setIsManualStart(false); // Reset flag after starting
    
    // Start from saved position when requested (onboarding only), otherwise from beginning.
    if (currentFlattened.length > 0 && currentFlattened[0]) {
      let startIndex = 0;
      let startSectionId = currentFlattened[0].sectionId;
      let startStepId = currentFlattened[0].stepId;

      if (
        requestedStartSectionId &&
        requestedStartStepId
      ) {
        const requestedIndex = currentFlattened.findIndex(
          step =>
            step.sectionId === requestedStartSectionId &&
            step.stepId === requestedStartStepId
        );
        if (requestedIndex !== -1) {
          startIndex = requestedIndex;
          startSectionId = currentFlattened[requestedIndex].sectionId;
          startStepId = currentFlattened[requestedIndex].stepId;
        }
      } else if (
        shouldResume &&
        mode === 'onboarding' &&
        tutorialData?.onboarding?.lastSectionId &&
        tutorialData?.onboarding?.lastStepId
      ) {
        const resumeIndex = currentFlattened.findIndex(
          step =>
            step.sectionId === tutorialData.onboarding.lastSectionId &&
            step.stepId === tutorialData.onboarding.lastStepId
        );
        if (resumeIndex !== -1) {
          startIndex = resumeIndex;
          startSectionId = currentFlattened[resumeIndex].sectionId;
          startStepId = currentFlattened[resumeIndex].stepId;
        }
      }

      setCurrentStepIndex(startIndex);
      setCurrentSectionId(startSectionId);
      setCurrentStepId(startStepId);

      const isFreshStart =
        startIndex === 0 &&
        mode === 'onboarding' &&
        !tutorialData.onboarding?.startedAt;

      if (isFreshStart) {
        await updateProgress(startSectionId, startStepId, 'start');
      } else {
        await updateProgress(startSectionId, startStepId, 'view_step');
      }
    }
  };

  const exitTutorial = async () => {
    
    // Send server log for tutorial close
    const tutorialType = mode === 'onboarding' ? 'full' : 'feature';
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: 'CLOSED_TUTORIAL',
        eventData: {
          tutorialType: tutorialType,
          sectionId: currentSectionId || '',
          stepId: currentStepId || '',
          pageId: pageId || '',
          patientId: '',
          mrn: ''
        }
      }
    });
    
    setIsActive(false);
    setIsManualStart(false);
    setStartedFromDashboard(false);
    setIsFullTutorial(false);
    setShouldAutoStart(false); // Clear auto-start flag to prevent tutorial from restarting

    if (mode === 'onboarding') {
      // Closing the onboarding tour — including the first-time auto-launch —
      // dismisses it for good: persist showOnLogin=false (via the 'skip'
      // action) so the full guide never auto-pops on later reloads, even if the
      // user exits before any step was recorded. They can still relaunch it
      // from the "i" button, which resumes from the saved position below.
      const closeSectionId =
        currentSectionId === 'review_upload'
          ? 'review_upload'
          : currentSectionId || null;
      const closeStepId =
        currentSectionId === 'review_upload'
          ? 'review_note_page'
          : currentStepId || null;
      // Update local state immediately so nothing re-triggers this session.
      setTutorialData((prev) => (prev ? { ...prev, showOnLogin: false } : prev));
      await updateProgress(closeSectionId, closeStepId, 'skip');
      return;
    }

    // Feature/page tutorials keep the existing close-to-resume behavior.
    if (currentSectionId === 'review_upload') {
      // Save progress as review_note_page so tutorial always restarts from there
      await updateProgress('review_upload', 'review_note_page', 'close');
    } else if (currentSectionId && currentStepId) {
      // Save current position with 'close' action for other sections
      await updateProgress(currentSectionId, currentStepId, 'close');
    }

  };

  const completeTutorial = async () => {
    const wasFullTutorial = isFullTutorial;
    setIsActive(false);
    setIsFullTutorial(false);
    setShouldAutoStart(false); // Clear auto-start flag to prevent tutorial from restarting
    setTutorialSteps([]); // Clear tutorial steps to prevent auto-restart

    // A completed full tour has walked through the Smart Chart slides (part of
    // dashboard_intro), so record the smart_chart page tutorial as seen. This
    // is what keeps the modal's first-open auto-launch from firing for users
    // who did the full tutorial. Await it so its PATCH is serialized with the
    // completion PATCH below (both read-modify-write webapp_walkthrough).
    if (wasFullTutorial && !tutorialData?.pageTutorials?.smart_chart?.viewed) {
      await markPageTutorialViewed('smart_chart');
    }

    try {
      const body = {
        action: 'complete',
        stepId: currentStepId || '',
      };
      
      if (mode === 'onboarding') {
        body.sectionId = currentSectionId;
      } else if (mode === 'page') {
        body.pageId = pageId;
      }
      
      const response = await patchTutorialProgress(body);

      if (!response.ok) {
        throw new Error(`Failed to complete tutorial: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to complete tutorial');
      }
      
      // Update local state
      if (mode === 'onboarding') {
        setTutorialData(prev => ({
          ...prev,
          onboarding: {
            ...prev.onboarding,
            completedAt: new Date().toISOString(),
          },
          showOnLogin: false,
        }));
      }
    } catch (error) {
      // Still update local state even if API call fails
      if (mode === 'onboarding') {
        setTutorialData(prev => ({
          ...prev,
          onboarding: {
            ...prev.onboarding,
            completedAt: new Date().toISOString(),
          },
          showOnLogin: false,
        }));
      }
    }
  };

  const nextStep = useCallback(() => {
    // Use functional update to ensure we have the latest state
    setCurrentStepIndex(prevIndex => {
      const currentFlattened = flattenedSteps; // Get latest flattened steps

      if (
        forcedStepRef.current === 'telehealth_microphone_step' &&
        Date.now() - forcedStepTimestampRef.current < 1000
      ) {
        return prevIndex;
      }
      
      if (prevIndex < currentFlattened.length - 1) {
        const newIndex = prevIndex + 1;
        const newStep = currentFlattened[newIndex];
        
        if (!newStep) {
          return prevIndex; // Don't advance if step not found
        }
        
        // Update section and step IDs
        setCurrentSectionId(newStep.sectionId);
        setCurrentStepId(newStep.stepId);
        
        updateProgress(newStep.sectionId, newStep.stepId, 'view_step');
        
        return newIndex;
    } else {
      // Tutorial completed
      completeTutorial();
        return prevIndex;
    }
    });
  }, [flattenedSteps]);

  const previousStep = () => {
    if (currentStepIndex > 0) {
      const newIndex = currentStepIndex - 1;
      const newStep = flattenedSteps[newIndex];
      
      setCurrentStepIndex(newIndex);
      setCurrentSectionId(newStep.sectionId);
      setCurrentStepId(newStep.stepId);
    }
  };

  const goToStep = (sectionId, stepId) => {
    const index = flattenedSteps.findIndex(
      step => step.sectionId === sectionId && step.stepId === stepId
    );
    
    if (index !== -1) {
      if (stepId === 'telehealth_microphone_step') {
        forcedStepRef.current = stepId;
        forcedStepTimestampRef.current = Date.now();
      }
      setCurrentStepIndex(index);
      setCurrentSectionId(sectionId);
      setCurrentStepId(stepId);
      updateProgress(sectionId, stepId, 'view_step');
    }
  };

  // Process tutorial steps when they're set
  useEffect(() => {
    if (!tutorialEnabled) {
      return;
    }
    if (tutorialSteps && tutorialSteps.length > 0) {
      const flattened = flattenTutorialSteps(tutorialSteps);

      // During an active full tutorial, never shrink the step list — a
      // page-scoped trigger that calls setTutorialSteps with a single section
      // must not overwrite the full list and reset the counter mid-flow.
      if (isActive && isFullTutorial && flattenedStepsRef.current.length > flattened.length) {
        return;
      }

      setFlattenedSteps(flattened);
      
      // Check if tutorial was started from dashboard (all sections included)
      // Dashboard returns all sections, other pages return specific sections
      const isFromDashboard = tutorialSteps.length === tutorialConfig.sections.length;
      setStartedFromDashboard(isFromDashboard);
      
      // Handle auto-start: if shouldAutoStart is true and we have steps, start the tutorial
      if (shouldAutoStart && flattened.length > 0 && !isActive) {
        // Set all state together - React will batch these updates
        setCurrentStepIndex(0);
        setCurrentSectionId(flattened[0].sectionId);
        setCurrentStepId(flattened[0].stepId);
        // Don't set shouldAutoStart to false yet - let the next useEffect handle it.
        // Progress is recorded there too, once the tour actually opens.
      } else if (!currentSectionId && flattened[0] && !isActive && !shouldAutoStart && !isManualStart) {
        // Set initial position if not resuming, not auto-starting, and not manually starting
        setCurrentSectionId(flattened[0].sectionId);
        setCurrentStepId(flattened[0].stepId);
      }
      
      // IMPORTANT: If tutorial is already active, don't reset the step index
      // This prevents the tutorial from jumping when steps are reloaded during navigation
      // BUT: Skip this if we're on record page and current step is before telehealth_setup
      // (The navigation useEffect will handle advancing to the correct step)
      const isOnRecordPage = location.pathname.startsWith('/record');
      const isInvalidStepForRecordPage = currentStepId && ['veteran_consent', 'ready_to_record'].includes(currentStepId);
      
      if (isActive && currentStepIndex !== null && currentStepId && !(isOnRecordPage && isInvalidStepForRecordPage)) {
        // Find the current step in the new flattened array
        const currentStepIndexInNewArray = flattened.findIndex(
          step => step.sectionId === currentSectionId && step.stepId === currentStepId
        );
        if (currentStepIndexInNewArray !== -1) {
          // Also check: don't reset if we're on record page and trying to go back to an invalid step
          if (!(isOnRecordPage && isInvalidStepForRecordPage)) {
            if (currentStepIndexInNewArray !== currentStepIndex) {
              setCurrentStepIndex(currentStepIndexInNewArray);
              // Also update sectionId and stepId to ensure consistency
              setCurrentSectionId(flattened[currentStepIndexInNewArray].sectionId);
              setCurrentStepId(flattened[currentStepIndexInNewArray].stepId);
            }
          }
        }
      }
    } else {
      // Steps were cleared (e.g. the tutorial launcher resets them before
      // loading a new subset): clear the flattened list too so a subsequent
      // startTutorial cannot start from a stale step list.
      setFlattenedSteps([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialSteps, shouldAutoStart, isActive, currentSectionId, isManualStart, tutorialEnabled, slideAccess]);

  useEffect(() => {
    flattenedStepsRef.current = flattenedSteps;
  }, [flattenedSteps]);

  // Separate useEffect to activate tutorial after flattenedSteps is set
  useEffect(() => {
    if (!tutorialEnabled) {
      return;
    }
    const isSlideAccessSettled = !isSlideAccessLoading || hasAutoStartWaitElapsed;
    if (shouldAutoStart && flattenedSteps.length > 0 && !isActive && isSlideAccessSettled) {
      setIsActive(true);
      setShouldAutoStart(false);
      // Recorded only now: a user who leaves before the tour opens has seen no
      // slide, and saved progress would stop it auto-starting next visit.
      updateProgress(flattenedSteps[0].sectionId, flattenedSteps[0].stepId, 'start');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flattenedSteps,
    shouldAutoStart,
    isActive,
    currentStepIndex,
    tutorialEnabled,
    isSlideAccessLoading,
    hasAutoStartWaitElapsed,
  ]);

  // Detect navigation to record page and automatically show telehealth step
  useEffect(() => {
    if (!tutorialEnabled) {
      return;
    }
    // Only proceed if tutorial is active
    if (!isActive || flattenedSteps.length === 0) {
      previousPathnameRef.current = location.pathname;
      return;
    }

    const currentPath = location.pathname;
    const previousPath = previousPathnameRef.current;

    // Check if we're on the record page
    if (currentPath.startsWith('/record')) {
      // Find the telehealth_setup step in the flattened steps
      const telehealthStepIndex = flattenedSteps.findIndex(
        step => step.stepId === 'telehealth_setup'
      );

      // Find current step index
      const currentStepIndex = flattenedSteps.findIndex(
        step => step.stepId === currentStepId
      );

      // If telehealth_setup is not in flattenedSteps, we need to ensure all sections are loaded
      // This can happen if tutorialSteps was filtered or doesn't include the record section
      if (telehealthStepIndex === -1) {
        const recordSection = tutorialSteps.find(s => s.id === 'record');
        
        // If record section is missing and we're in onboarding mode, reload all sections
        if (!recordSection && mode === 'onboarding') {
          setTutorialSteps(tutorialConfig.sections);
          return; // Let the useEffect re-run with new steps
        }
      }

      // If we're on record page, always advance to telehealth_setup if current step is veteran_consent or ready_to_record
      // OR if navigating from dashboard to record page (for full tutorial flow)
      if (telehealthStepIndex !== -1) {
        const isInvalidStepForRecordPage = currentStepId && ['veteran_consent', 'ready_to_record'].includes(currentStepId);
        const telehealthStepIds = [
          'telehealth_setup',
          'telehealth_microphone_step',
          'telehealth_screen_audio_step',
          'telehealth_close_modal'
        ];
        const isInTelehealthFlow = currentStepId && telehealthStepIds.includes(currentStepId);
        const isDashboardToRecord = previousPath === '/dashboard' && currentPath.startsWith('/record') && startedFromDashboard;
        
        // Only advance if:
        // 1. Current step is explicitly invalid for record page (veteran_consent, ready_to_record)
        // 2. OR we're navigating from dashboard to record page (full tutorial flow)
        // Don't advance if we're already past telehealth_setup or if we're on a step that's valid for record page
        const shouldAdvance = 
          isInvalidStepForRecordPage || // Explicitly check for steps that shouldn't show on record page
          isDashboardToRecord; // Original logic for dashboard -> record navigation

        if (shouldAdvance && !isInTelehealthFlow && currentStepId !== 'telehealth_setup') {
          // Use setTimeout to ensure this runs after any step maintenance logic
          setTimeout(() => {
            setCurrentStepIndex(telehealthStepIndex);
            setCurrentSectionId(flattenedSteps[telehealthStepIndex].sectionId);
            setCurrentStepId('telehealth_setup');
            updateProgress(flattenedSteps[telehealthStepIndex].sectionId, 'telehealth_setup', 'view_step');
          }, 0);
        }
      }
    }

    previousPathnameRef.current = currentPath;
  }, [location.pathname, isActive, flattenedSteps, startedFromDashboard, currentStepId, currentStepIndex, tutorialEnabled]);

  if (!tutorialEnabled) {
    return <>{children}</>;
  }

  const value = {
    // State
    isActive,
    isLoading,
    // Exposed so consumers can tell "the guide is finished" apart from "the
    // guide is a render away from opening" -- isActive is false for both.
    shouldAutoStart,
    mode,
    pageId,
    currentSectionId,
    currentStepId,
    currentStepIndex,
    tutorialData,
    tutorialSteps,
    flattenedSteps,
    patientDescription,
    isFullTutorial,

    // Actions
    setTutorialSteps,
    startTutorial,
    exitTutorial,
    completeTutorial,
    nextStep,
    previousStep,
    goToStep,
    setPatientDescription,
    markPageTutorialViewed,

    // Computed
    totalSteps: flattenedSteps.length,
    progress: flattenedSteps.length > 0 
      ? ((currentStepIndex + 1) / flattenedSteps.length) * 100 
      : 0,
    isFirstStep: currentStepIndex === 0,
    isLastStep: currentStepIndex === flattenedSteps.length - 1,
    currentStep: flattenedSteps[currentStepIndex] || null
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};